import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';

// GDP: Expenditure Approach (Y = C + I + G + NX) & Income Approach (Y = W + R + i + P + Statistical Adjustments)
export default function GDPCalc() {
  const [mode, setMode] = useState('exp'); // exp | inc
  // Expenditure
  const [c, setC] = useState(12000);
  const [i, setI] = useState(3500);
  const [g, setG] = useState(4500);
  const [ex, setEx] = useState(2500);
  const [im, setIm] = useState(3000);
  // Income (Resource Cost-Income Approach) — per Calculator.net reference
  const [empComp, setEmpComp] = useState(10000);
  const [propInc, setPropInc] = useState(1500);
  const [rentInc, setRentInc] = useState(1000);
  const [corpProf, setCorpProf] = useState(2000);
  const [intInc, setIntInc] = useState(1200);
  const [indTax, setIndTax] = useState(1500);
  const [depr, setDepr] = useState(2500);
  const [netFor, setNetFor] = useState(500);

  const gdp = useMemo(() => {
    if (mode === 'exp') return Number(c) + Number(i) + Number(g) + (Number(ex) - Number(im));
    const gnp = Number(empComp) + Number(propInc) + Number(rentInc) + Number(corpProf) + Number(intInc);
    return { gnp, gdp: gnp + Number(indTax) + Number(depr) + Number(netFor) };
  }, [mode, c, i, g, ex, im, empComp, propInc, rentInc, corpProf, intInc, indTax, depr, netFor]);

  return (
    <CalcShell look="cnet" title="GDP Calculator" subtitle="Compute Gross Domestic Product using the Expenditure or Income (Resource Cost) approach.">
      <div className="flex gap-2 border-b mb-4">
        {[['exp','Expenditure Approach'],['inc','Resource Cost-Income Approach']].map(([v,l]) => (
          <button key={v} className={`px-4 py-2 text-sm font-semibold ${mode===v?'border-b-2 border-blue-600 text-blue-700':'text-slate-500'}`} onClick={() => setMode(v)}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          {mode === 'exp' ? (
            <>
              <div className="field"><label>Consumption (C)</label><input type="number" value={c} onChange={e => setC(e.target.value)} data-testid="gdp-c"/></div>
              <div className="field"><label>Investment (I)</label><input type="number" value={i} onChange={e => setI(e.target.value)}/></div>
              <div className="field"><label>Government Spending (G)</label><input type="number" value={g} onChange={e => setG(e.target.value)}/></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="field"><label>Exports</label><input type="number" value={ex} onChange={e => setEx(e.target.value)}/></div>
                <div className="field"><label>Imports</label><input type="number" value={im} onChange={e => setIm(e.target.value)}/></div>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-slate-700 mb-3"><b>Using this approach:</b><br/>
                <span className="font-mono">GNP = employee compensation + proprietors' income + rental income + corporate profits + interest income</span><br/>
                <span className="font-mono">GDP = GNP + indirect business taxes + depreciation + net income of foreigners*</span>
              </p>
              <div className="field"><label>Employee Compensation</label><input data-testid="gdp-emp" type="number" value={empComp} onChange={e => setEmpComp(e.target.value)} /></div>
              <div className="field"><label>Proprietors' Income</label><input type="number" value={propInc} onChange={e => setPropInc(e.target.value)} /></div>
              <div className="field"><label>Rental Income</label><input type="number" value={rentInc} onChange={e => setRentInc(e.target.value)} /></div>
              <div className="field"><label>Corporate Profits</label><input type="number" value={corpProf} onChange={e => setCorpProf(e.target.value)} /></div>
              <div className="field"><label>Interest Income</label><input type="number" value={intInc} onChange={e => setIntInc(e.target.value)} /></div>
              <div className="field"><label>Indirect Business Taxes</label><input type="number" value={indTax} onChange={e => setIndTax(e.target.value)} /></div>
              <div className="field"><label>Depreciation</label><input type="number" value={depr} onChange={e => setDepr(e.target.value)} /></div>
              <div className="field"><label>Net Income of Foreigners*</label><input type="number" value={netFor} onChange={e => setNetFor(e.target.value)} /></div>
              <p className="text-xs text-slate-500 mt-1">* net income of foreigners refers to the income that domestic citizens earn abroad subtracted from the income foreigners earn domestically.</p>
            </>
          )}
        </div>
        <div>
          <div className="p-5 rounded-xl bg-indigo-50 border border-indigo-200 text-center">
            <div className="text-sm text-slate-600">GDP</div>
            <div className="text-4xl font-bold text-indigo-700" data-testid="gdp-result">${(mode === 'exp' ? gdp : gdp.gdp).toLocaleString()}</div>
            <div className="text-xs text-slate-500 mt-2 font-mono">
              {mode === 'exp' ? 'Y = C + I + G + (X − M)' : 'GDP = GNP + Indirect Taxes + Depreciation + Net Foreign Income'}
            </div>
            {mode === 'inc' && (
              <div className="mt-3 text-sm text-slate-700">
                GNP = <b>${gdp.gnp.toLocaleString()}</b>
              </div>
            )}
          </div>
        </div>
      </div>
    </CalcShell>
  );
}
